import React, { useCallback, useEffect, useMemo, useState } from 'react';
import supabase from '../../config/supabase';
import { fetchSessionHubStatuses, listDraftPOsForSessions } from '../../services/orderingHubService';
import { fetchOpenPlansForSessions } from '../../services/purchasingPlanService';
import { applyActiveFilter } from '../../utils/sessionLifecycle';
import { isStoreOwnedBySuperAdmin, SUPER_ADMIN_USER_IDS, COUNTERTOP_APP_CREATOR_LABEL } from '../../utils/userDisplay';

const HUB_STATUS_LABELS = {
  hub_send: 'Sent',
  hub_generate_po: 'PO Generated',
  hub_print: 'Printed',
  hub_cancel: 'Cancelled',
  hub_edit: 'Edited',
};

const HUB_DONE_ACTIONS = new Set(['hub_send', 'hub_generate_po']);

const formatHubStatusTooltip = (status) => {
  if (!status) return 'No Ordering Hub action taken yet';
  const label = HUB_STATUS_LABELS[status.lastAction] || status.lastAction;
  let when = '';
  try {
    when = new Date(status.lastAt).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { when = String(status.lastAt || ''); }
  return when ? `${label} · ${when}` : label;
};

const todayYmd = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const daysAgoYmd = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  } catch { return String(value); }
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return String(value); }
};

const formatDuration = (startedAt, completedAt) => {
  if (!startedAt || !completedAt) return '—';
  try {
    const ms = new Date(completedAt) - new Date(startedAt);
    if (ms <= 0) return '—';
    const totalMins = Math.round(ms / 60000);
    if (totalMins < 60) return `${totalMins}m`;
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  } catch { return '—'; }
};

const shortId = (id) => (id ? String(id).slice(0, 8) : '—');

const formatUserCompact = (p) => {
  if (!p) return null;
  // Editable team-facing display name wins; legacy first/last is the fallback.
  const display = (p.display_name || '').trim();
  if (display) {
    const parts = display.split(/\s+/).filter(Boolean);
    return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : parts[0];
  }
  const first = (p.first_name || '').trim();
  const last = (p.last_name || '').trim();
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return null;
};

export default function CompletedCountsList({ storeId, currentUserId, onReview, onOpenDraft, onOpenSavingsReview }) {
  const [fromDate, setFromDate] = useState(daysAgoYmd(29));
  const [toDate, setToDate] = useState(todayYmd());
  const [sessions, setSessions] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [zeroCountMap, setZeroCountMap] = useState({});
  const [hubStatusMap, setHubStatusMap] = useState({});
  const [hubStatusLoading, setHubStatusLoading] = useState(false);
  const [draftPoMap, setDraftPoMap] = useState({});
  // count_session_id → open savings-review plan. A count whose order is
  // waiting in the Purchase Planner should say so here, and its action should
  // lead there — not to a second, parallel way of creating the same POs.
  const [savingsPlanMap, setSavingsPlanMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checked, setChecked] = useState({});

  const load = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError('');
    try {
      let query = applyActiveFilter(supabase
        .from('count_sessions')
        .select('id, name, created_at, completed_at, started_at, session_date, total_items, items_counted, created_by, notes, store_id, template_name_snapshot, template_type_snapshot')
        .eq('store_id', storeId)
        .eq('status', 'completed'))
        .order('completed_at', { ascending: false })
        .limit(100);

      // Filter by when the count was actually COMPLETED, not session_date.
      // session_date is the scheduled date and can sit in the future (a count
      // planned for next week, finished early) — filtering on it made exactly
      // those counts vanish from this list the moment they completed, while
      // the list sorted by completed_at all along.
      if (fromDate) query = query.gte('completed_at', fromDate);
      if (toDate) {
        const end = new Date(`${toDate}T00:00:00`);
        end.setDate(end.getDate() + 1);
        query = query.lt('completed_at', end.toISOString().slice(0, 10));
      }

      const { data, error: qErr } = await query;
      if (qErr) throw new Error(qErr.message);
      const rows = data || [];
      setSessions(rows);
      setChecked({});

      const sessionIds = rows.map((s) => s.id);

      // Batch-resolve zero-count totals for all sessions in a single query
      if (sessionIds.length > 0) {
        supabase
          .from('count_items')
          .select('count_session_id')
          .in('count_session_id', sessionIds)
          .eq('is_counted', true)
          .eq('current_count', 0)
          .then(({ data: zeroRows }) => {
            const map = {};
            sessionIds.forEach((id) => { map[id] = 0; });
            (zeroRows || []).forEach((r) => {
              map[r.count_session_id] = (map[r.count_session_id] || 0) + 1;
            });
            setZeroCountMap(map);
          })
          .catch(() => {});
      } else {
        setZeroCountMap({});
      }

      // Batch-resolve Ordering Hub action history for status badges
      if (sessionIds.length > 0) {
        setHubStatusLoading(true);
        fetchSessionHubStatuses(sessionIds)
          .then((map) => setHubStatusMap(map || {}))
          .catch(() => setHubStatusMap({}))
          .finally(() => setHubStatusLoading(false));
      } else {
        setHubStatusMap({});
        setHubStatusLoading(false);
      }

      // Batch-resolve existing mobile-created draft POs for the "Draft ready" indicator
      if (sessionIds.length > 0) {
        listDraftPOsForSessions(sessionIds, storeId)
          .then((map) => setDraftPoMap(map || {}))
          .catch(() => setDraftPoMap({}));
      } else {
        setDraftPoMap({});
      }

      // Batch-resolve open purchase plans so those counts point at the
      // worksheet instead of the classic per-vendor draft path.
      if (sessionIds.length > 0) {
        fetchOpenPlansForSessions(storeId, sessionIds)
          .then((map) => setSavingsPlanMap(map || {}))
          .catch(() => setSavingsPlanMap({}));
      } else {
        setSavingsPlanMap({});
      }

      // Counts the super admin created in a store they don't own read as
      // "Countertop App" rather than the admin's name in the "Counted By" column.
      const adminOwnsStore = await isStoreOwnedBySuperAdmin(supabase, storeId);

      // Batch-resolve user display names for the "Counted By" column
      const uids = [...new Set(rows.map((s) => s.created_by).filter(Boolean))];
      if (uids.length > 0) {
        supabase
          .from('user_profiles')
          .select('id, display_name, first_name, last_name, email')
          .in('id', uids)
          .then(({ data: profiles }) => {
            const map = {};
            (profiles || []).forEach((p) => {
              map[p.id] = formatUserCompact(p) || p.email || shortId(p.id);
            });
            if (!adminOwnsStore) {
              SUPER_ADMIN_USER_IDS.forEach((id) => { map[id] = COUNTERTOP_APP_CREATOR_LABEL; });
            }
            setUserMap(map);
          })
          .catch(() => setUserMap({}));
      } else {
        setUserMap({});
      }
    } catch (err) {
      setError(err?.message || 'Failed to load completed counts.');
    } finally {
      setLoading(false);
    }
  }, [storeId, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const toggleAll = () => {
    if (allChecked) {
      setChecked({});
    } else {
      const next = {};
      sessions.forEach((s) => { next[s.id] = true; });
      setChecked(next);
    }
  };

  const toggleOne = (id) => {
    setChecked((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id]; else next[id] = true;
      return next;
    });
  };

  const selectedIds = Object.keys(checked).filter((k) => checked[k]);
  const selectedSessions = sessions.filter((s) => checked[s.id]);
  const allChecked = sessions.length > 0 && sessions.every((s) => checked[s.id]);
  const someChecked = selectedIds.length > 0 && !allChecked;

  const handleReview = () => {
    if (selectedSessions.length === 0) return;
    onReview(selectedSessions);
  };

  return (
    <div style={shellStyle}>
      <header style={headerStyle}>
        <div>
          <h2 style={titleStyle}>Completed Counts</h2>
          <div style={subtitleStyle}>
            Select one or more count sessions to review and dispatch orders. Reviews are done one session at a time.
          </div>
        </div>
      </header>

      <div style={filtersRowStyle}>
        <div style={filterFieldStyle}>
          <label style={filterLabelStyle}>From</label>
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputStyle} />
        </div>
        <div style={filterFieldStyle}>
          <label style={filterLabelStyle}>To</label>
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputStyle} />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <button type="button" style={pillStyle} onClick={() => { setFromDate(daysAgoYmd(6)); setToDate(todayYmd()); }}>Last 7 days</button>
          <button type="button" style={pillStyle} onClick={() => { setFromDate(daysAgoYmd(29)); setToDate(todayYmd()); }}>Last 30 days</button>
          <button type="button" style={pillStyle} onClick={() => { setFromDate(''); setToDate(''); }}>All time</button>
          <button type="button" style={refreshBtnStyle} onClick={load}>Refresh</button>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div style={selectionBarStyle}>
          <span style={{ fontWeight: 600 }}>{selectedIds.length} session{selectedIds.length === 1 ? '' : 's'} selected</span>
          <button type="button" style={reviewBtnStyle} onClick={handleReview}>
            Review Selected →
          </button>
        </div>
      )}

      <div style={{ background: '#fff', border: '1.5px solid #E9DFD6', borderRadius: 12, overflow: 'hidden' }}>
        <table style={tableStyle} className="ohub-counts-table">
          <thead>
            <tr style={{ background: '#FAF6F2' }}>
              <th style={thStyle}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = someChecked; }}
                  onChange={toggleAll}
                  disabled={sessions.length === 0}
                  aria-label="Select all"
                />
              </th>
              <th style={thStyle}>Count Name</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Count Date</th>
              <th style={thStyle}>Counted By</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Duration</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Total Items</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Not Counted</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Zero Counts</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} style={emptyTdStyle}>Loading…</td></tr>
            )}
            {!loading && error && (
              <tr><td colSpan={11} style={errorTdStyle}>{error}</td></tr>
            )}
            {!loading && !error && sessions.length === 0 && (
              <tr>
                <td colSpan={11} style={emptyTdStyle}>
                  No completed count sessions found. Try widening the date range.
                </td>
              </tr>
            )}
            {!loading && !error && sessions.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                checked={!!checked[s.id]}
                onToggle={() => toggleOne(s.id)}
                onReview={() => onReview([s])}
                countedBy={
                  s.created_by
                    ? (userMap[s.created_by] || (s.created_by === currentUserId ? 'You' : shortId(s.created_by)))
                    : '—'
                }
                zeroCounts={zeroCountMap[s.id] ?? null}
                hubStatus={hubStatusMap[s.id] || null}
                hubStatusLoading={hubStatusLoading}
                draftPos={draftPoMap[s.id] || []}
                onOpenDraft={onOpenDraft}
                savingsPlan={savingsPlanMap[s.id] || null}
                onOpenSavingsReview={onOpenSavingsReview}
              />
            ))}
          </tbody>
        </table>
      </div>
      <style>{mobileCss}</style>
    </div>
  );
}

function SessionRow({ session, checked, onToggle, onReview, countedBy, zeroCounts, hubStatus, hubStatusLoading, draftPos, onOpenDraft, savingsPlan, onOpenSavingsReview }) {
  const notCounted = Math.max(0, (session.total_items || 0) - (session.items_counted || 0));
  const duration = formatDuration(session.started_at, session.completed_at);
  const isDone = !!hubStatus && HUB_DONE_ACTIONS.has(hubStatus.lastAction);
  // Also treat sessions where any send/PO event ever occurred as "done" even
  // if a later edit/print/cancel was the most recent action.
  const everSentOrPo = !!hubStatus && (hubStatus.hasSend || hubStatus.hasGeneratePo);
  const hasDraft = Array.isArray(draftPos) && draftPos.length > 0;

  return (
    <tr style={rowStyle(checked, everSentOrPo)} className="ohub-counts-row" onClick={() => onToggle()}>
      <td style={tdStyle} className="ohub-cell-checkbox" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${session.name}`}
        />
      </td>
      <td style={tdStyle} className="ohub-cell-name">
        <div style={{ fontWeight: 600, color: everSentOrPo ? '#5A4A42' : '#3D2518', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {session.name || `Session ${session.id}`}
          {hasDraft && (
            <span style={draftReadyBadgeStyle} title={`${draftPos.length} draft order${draftPos.length === 1 ? '' : 's'} ready`}>
              Draft ready
            </span>
          )}
          {savingsPlan && (
            <span style={savingsReviewBadgeStyle} title="This count's order is waiting in the Purchase Planner">
              Purchase plan ready
            </span>
          )}
        </div>
        {session.template_name_snapshot && (
          <div style={{ color: '#8B7B73', fontSize: 11, marginTop: 2, fontStyle: 'italic' }}>
            {session.template_name_snapshot}{session.template_type_snapshot ? ` · ${session.template_type_snapshot}` : ''}
          </div>
        )}
        {session.notes && (
          <div className="ohub-cell-notes" style={{ color: '#8B7B73', fontSize: 12, marginTop: 2, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {session.notes}
          </div>
        )}
      </td>
      <td style={tdStyle} data-label="Status">
        <HubStatusBadge status={hubStatus} loading={hubStatusLoading} done={isDone} />
      </td>
      <td style={tdStyle} data-label="Count Date">{formatDateTime(session.completed_at || session.session_date)}</td>
      <td style={{ ...tdStyle, color: '#5A4A42' }} data-label="Counted By">{countedBy}</td>
      <td style={tdStyle} data-label="Created">{formatDate(session.created_at)}</td>
      <td style={tdStyle} data-label="Duration">{duration}</td>
      <td style={{ ...tdStyle, textAlign: 'right' }} data-label="Total Items">{session.total_items ?? '—'}</td>
      <td style={{ ...tdStyle, textAlign: 'right', color: notCounted > 0 ? '#bf360c' : 'inherit' }} data-label="Not Counted">
        {notCounted}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', color: zeroCounts > 0 ? '#bf360c' : 'inherit' }} data-label="Zero Counts">
        {zeroCounts === null ? '…' : zeroCounts}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right' }} className="ohub-cell-action" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {savingsPlan && (
            <button
              type="button"
              data-testid="counts-list-open-savings-review"
              style={savingsReviewBtnStyle}
              onClick={() => {
                if (onOpenSavingsReview) onOpenSavingsReview(savingsPlan.id);
                else window.location.href = `ordering.html?savings=${encodeURIComponent(savingsPlan.id)}`;
              }}
              title="Compare vendors and create this count's purchase orders"
            >
              Open Purchase Planner →
            </button>
          )}
          {hasDraft && onOpenDraft && draftPos.map((draft) => (
            <button
              key={draft.id}
              type="button"
              style={openDraftBtnStyle}
              onClick={() => onOpenDraft(draft)}
              title={`Open draft for ${draft.vendor_name_snapshot || 'vendor'}`}
            >
              {draftPos.length === 1 ? 'Open Draft →' : `${draft.vendor_name_snapshot || 'Draft'} →`}
            </button>
          ))}
          <button
            type="button"
            style={rowReviewBtnStyle}
            onClick={() => onReview()}
          >
            Review →
          </button>
        </div>
      </td>
    </tr>
  );
}

const shellStyle = {
  padding: '24px 28px',
  maxWidth: 1280,
  margin: '0 auto',
  fontFamily: 'Open Sans, sans-serif',
  color: '#3D2518',
};
const headerStyle = { marginBottom: 20 };
const titleStyle = {
  fontFamily: 'Montserrat, sans-serif',
  fontSize: 24,
  fontWeight: 800,
  color: '#3D2518',
  margin: 0,
};
const subtitleStyle = { color: '#8B7B73', fontSize: 14, marginTop: 4 };
const filtersRowStyle = {
  display: 'flex',
  gap: 16,
  alignItems: 'flex-end',
  flexWrap: 'wrap',
  background: '#fff',
  border: '1.5px solid #E9DFD6',
  borderRadius: 12,
  padding: 14,
  marginBottom: 12,
};
const filterFieldStyle = { display: 'flex', flexDirection: 'column' };
const filterLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: '#5A4A42',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 4,
};
const inputStyle = {
  padding: '8px 10px',
  border: '1.5px solid #E9DFD6',
  borderRadius: 8,
  fontSize: 13,
  color: '#3D2518',
  background: '#fff',
};
const pillStyle = {
  background: '#fff',
  border: '1.5px solid #d8cdc3',
  color: '#5A4A42',
  padding: '6px 12px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
};
const refreshBtnStyle = {
  background: '#526B7C',
  color: '#fff',
  border: '1px solid #3F5563',
  padding: '6px 14px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
};
const selectionBarStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 16px',
  background: '#EBF2F7',
  border: '1.5px solid #b0c9d8',
  borderRadius: 10,
  marginBottom: 12,
  fontSize: 14,
  color: '#3D2518',
};
const reviewBtnStyle = {
  background: '#526B7C',
  color: '#fff',
  border: '1px solid #3F5563',
  padding: '8px 18px',
  borderRadius: 8,
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
};
const rowReviewBtnStyle = {
  background: '#fff',
  border: '1.5px solid #526B7C',
  color: '#526B7C',
  padding: '5px 12px',
  borderRadius: 7,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const openDraftBtnStyle = {
  background: '#526B7C',
  border: '1.5px solid #3F5563',
  color: '#fff',
  padding: '5px 12px',
  borderRadius: 7,
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const savingsReviewBadgeStyle = {
  fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
  background: '#FDF6EC', color: '#7b5a2a', border: '1px solid #EAD9BE',
  borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap',
};

const savingsReviewBtnStyle = {
  borderRadius: 999, padding: '6px 12px', border: '2px solid #5A3A28',
  background: '#3A2418', color: '#fff', fontWeight: 700, fontSize: 12,
  cursor: 'pointer', whiteSpace: 'nowrap',
};

const draftReadyBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  background: '#E3F0FF',
  color: '#1F4D6B',
  border: '1px solid #93C5E8',
  borderRadius: 999,
  padding: '2px 8px',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.03em',
  whiteSpace: 'nowrap',
};
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: '#5A4A42',
  borderBottom: '2px solid #E9DFD6',
  whiteSpace: 'nowrap',
};
const tdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #f3ebe2',
  color: '#3D2518',
  verticalAlign: 'middle',
};
const rowStyle = (checked, done) => ({
  background: checked ? '#EBF2F7' : (done ? '#FAF6F2' : '#fff'),
  cursor: 'pointer',
  transition: 'background 0.12s',
});

const badgeBaseStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 9px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap',
  border: '1px solid transparent',
};

const badgeStyles = {
  hub_send: { ...badgeBaseStyle, background: '#E6F4EA', color: '#1B5E20', borderColor: '#A5D6A7' },
  hub_generate_po: { ...badgeBaseStyle, background: '#E6F4EA', color: '#1B5E20', borderColor: '#A5D6A7' },
  hub_print: { ...badgeBaseStyle, background: '#EAF1F8', color: '#1F4D6B', borderColor: '#B6CFE0' },
  hub_edit: { ...badgeBaseStyle, background: '#FFF4E0', color: '#7A4F00', borderColor: '#F2D38B' },
  hub_cancel: { ...badgeBaseStyle, background: '#FDECEA', color: '#8B1A10', borderColor: '#F0B5AF' },
  none: { ...badgeBaseStyle, background: '#F3EBE2', color: '#8B7B73', borderColor: '#E9DFD6' },
  loading: { ...badgeBaseStyle, background: '#F3EBE2', color: '#B0A097', borderColor: '#E9DFD6' },
};

function HubStatusBadge({ status, loading, done }) {
  if (loading && !status) {
    return <span style={badgeStyles.loading}>…</span>;
  }
  if (!status) {
    return <span style={badgeStyles.none} title="No Ordering Hub action taken yet">Not started</span>;
  }
  const style = badgeStyles[status.lastAction] || badgeStyles.none;
  const label = HUB_STATUS_LABELS[status.lastAction] || 'Reviewed';
  return (
    <span style={style} title={formatHubStatusTooltip(status)}>
      {done ? <span aria-hidden="true">✓</span> : null}
      {label}
    </span>
  );
}
const emptyTdStyle = {
  padding: 32,
  textAlign: 'center',
  color: '#8B7B73',
};
const errorTdStyle = {
  padding: 16,
  color: '#b71c1c',
  background: '#fdecea',
};

const mobileCss = `
@media (max-width: 640px) {
  .ohub-counts-table { display: block; }
  .ohub-counts-table thead { display: none; }
  .ohub-counts-table tbody { display: block; }
  .ohub-counts-table tr { display: block; }
  .ohub-counts-table tr.ohub-counts-row {
    display: grid !important;
    grid-template-columns: auto 1fr;
    grid-column-gap: 10px;
    padding: 12px 14px !important;
    border-bottom: 1px solid #f3ebe2;
    background: #fff;
  }
  .ohub-counts-table tr.ohub-counts-row td {
    padding: 4px 0 !important;
    border-bottom: none !important;
    text-align: left !important;
    display: flex;
    justify-content: space-between;
    align-items: center;
    grid-column: 1 / -1;
    font-size: 13px;
  }
  .ohub-counts-table tr.ohub-counts-row td[data-label]::before {
    content: attr(data-label);
    color: #8B7B73;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-right: 12px;
    flex-shrink: 0;
  }
  .ohub-counts-table tr.ohub-counts-row td.ohub-cell-checkbox {
    grid-column: 1;
    grid-row: 1;
    padding: 0 !important;
    align-self: start;
  }
  .ohub-counts-table tr.ohub-counts-row td.ohub-cell-name {
    grid-column: 2;
    grid-row: 1;
    display: block;
    padding-bottom: 8px !important;
  }
  .ohub-counts-table tr.ohub-counts-row td.ohub-cell-name .ohub-cell-notes {
    max-width: none !important;
    white-space: normal !important;
  }
  .ohub-counts-table tr.ohub-counts-row td.ohub-cell-action {
    padding-top: 10px !important;
    justify-content: flex-end;
  }
}
`;
